import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { LIGHT_TOKENS } from './light-tokens';
import { PAPER_RULE, PAPER_INK } from '../ui/paper-tokens';

export type LightEyebrowProps = {
  children: ReactNode;
  className?: string;
  /**
   * 2026-07-17 R7 integration seam (design contract L3.1 / brief §2f,
   * "MUST-FIX"): additive, default `'light'` (byte-identical to every
   * existing consumer). `surface="paper"` re-inks the pill border/fill/
   * text to the warm `PAPER_*` family (`LightBand`'s own doc comment has
   * the full seam rationale) — the fill stays a hue-neutral `bg-black/
   * [0.04]` alpha overlay (identical value either way, so it is not
   * duplicated per-surface below), since `paper-tokens.ts` exports no `bg`
   * form for an ink tone (same documented registry gap `SocialProofSection.
   * tsx`'s `UnavailableStat` already flags for `BigStat`'s left-rule).
   */
  surface?: 'light' | 'paper';
};

/**
 * LightEyebrow — the neutralized pill eyebrow for light bands (masterplan
 * §1.2 "Light eyebrow chip"): attio's pill eyebrow (`"Build to scale"`)
 * NEUTRALIZED — attio's was blue-tinted; Law #4 keeps light bands ink-only,
 * no chapter/brand hue.
 */
export function LightEyebrow({ children, className, surface = 'light' }: LightEyebrowProps) {
  return (
    <span
      data-primitive="LightEyebrow"
      data-surface={surface}
      className={cn(
        surface === 'paper'
          ? cn(
              'inline-flex items-center gap-1.5 rounded-full border bg-black/[0.04] px-2.5 py-1',
              'text-[11px] font-semibold uppercase tracking-[0.08em] tabular-nums',
              PAPER_RULE.hairline.border,
              PAPER_INK.muted.text
            )
          : LIGHT_TOKENS.eyebrowChip,
        className
      )}
    >
      {children}
    </span>
  );
}
